"use client";
import { isMobile } from "react-device-detect";
import { useEffect, useState, useRef, useCallback } from "react";
import Link from "next/link";
import { TrashIcon } from "~/_components/icons";
import { useAppStore } from "~/store/app-store";
import { useUserStore } from "~/store/user-store";
import { formatInTimeZone } from "date-fns-tz";
import { CloseIcon, ShareIcon } from "~/_components/icons";
import { getUrlSlug, formatTag } from "~/_services/helpers";
import {
  BarIcon,
  EventIcon,
  ActivityIcon,
  RestaurantIcon,
} from "~/_components/icons";
import {
  ListingType,
  FUNDAYLIST_URL,
  AppI,
  LikedListingI,
  UserI,
} from "~/interfaces";
import FunImage from "~/_components/fun-image";
import { useShare } from "~/_hooks/useShare";

export default function LikeMenu() {
  const likeMenuRef = useRef<HTMLDivElement>(null);
  const likeMenuTitleRef = useRef<HTMLDivElement>(null);
  const likeMenuRemoveBtnRef = useRef<HTMLDivElement>(null);
  const appStore: AppI = useAppStore((state) => state.app);
  const userStore: UserI = useUserStore((state) => state.user);
  const removeLikedListing = useUserStore((state) => state.removeLikedListing);
  const removeAllLikedListings = useUserStore(
    (state) => state.removeAllLikedListings,
  );
  const updateMenu = useAppStore((state) => state.updateMenu);
  const [menuHeight, setMenuHeight] = useState(10);
  const share = useShare();

  const handleShare = (listingId: string, listingTitle: string) => {
    share({
      title: listingTitle,
      text: `Use FundayList to find fun things to do like ${listingTitle}.`,
      url: `https://${appStore.city?.name}.${FUNDAYLIST_URL}/listings/${listingId}?title=${getUrlSlug(listingTitle)}`,
    });
  };
  const getIcon = (listingType: string) => {
    return ListingType.ACTIVITY === listingType ? (
      <ActivityIcon className="h-[.80rem] pr-2 md:h-[.90rem] lg:h-4" />
    ) : ListingType.BAR === listingType ? (
      <BarIcon className="h-[.80rem] pr-2 md:h-[.90rem] lg:h-4" />
    ) : ListingType.EVENT === listingType ? (
      <EventIcon className="h-[.80rem] pr-2 md:h-[.90rem] lg:h-4" />
    ) : ListingType.RESTAURANT === listingType ? (
      <RestaurantIcon className="h-[.80rem] pr-2 md:h-[.90rem] lg:h-4" />
    ) : (
      ""
    );
  };

  useEffect(() => {
    // guard
    if (
      !likeMenuRef.current &&
      !likeMenuTitleRef.current &&
      !likeMenuRemoveBtnRef.current
    )
      return;

    const menuTop = likeMenuRef.current?.getBoundingClientRect().top ?? 0;
    const titleHeight =
      likeMenuTitleRef.current?.getBoundingClientRect().height ?? 0;
    const removeBtnHeight =
      likeMenuRemoveBtnRef.current?.getBoundingClientRect().height ?? 0;

    setMenuHeight(menuTop + titleHeight + removeBtnHeight);
  }, [appStore.menu.isLikeMenuActive]);

  return (
    <div
      ref={likeMenuRef}
      className={`${appStore.menu.isLikeMenuActive ? `right-0` : `-right-[24rem] lg:-right-[32rem]`} fun-like-menu transition-right absolute top-14 z-10 flex w-full max-w-[24rem] flex-col overflow-y-hidden border-t border-brown bg-beige font-[600] uppercase text-brown duration-300 ease-linear lg:top-20 lg:max-w-[32rem]`}
    >
      <div
        ref={likeMenuTitleRef}
        className="fun-box-shadow-bottom relative z-10 flex h-12 justify-center p-4 lg:h-14"
      >
        <span className="text-[1.2rem] font-[600] tracking-[1] lg:text-[1.6rem]">
          Favorites
        </span>
        <span
          className="absolute right-0 top-0 px-[.9rem] py-[.7rem] lg:px-[1.35rem] lg:py-[.7rem]"
          aria-label="close like menu"
          onClick={() => {
            updateMenu({ isLikeMenuActive: false });
          }}
        >
          <CloseIcon className="h-7 cursor-pointer text-brown lg:h-9" />
        </span>
      </div>

      {userStore.likedListings.length === 0 ? (
        <div
          className={`flex h-[calc(100dvh_-_152px)] flex-col px-4 py-8 text-center leading-7 lg:h-[calc(100dvh_-_200px)]`}
        >
          You have no favorite{" "}
          <span className="whitespace-nowrap">listings saved.</span>
          Click hearts to save them.
        </div>
      ) : (
        <>
          <ul
            style={{ height: `calc(100dvh - ${menuHeight}px)` }}
            className={`fun-scrollbar overflow-x-hidden overflow-y-scroll px-0 py-4`}
          >
            {userStore.likedListings.map((likedListing: LikedListingI) => {
              return (
                <li
                  key={`like-${likedListing.id}`}
                  fun-id="fun-like-item"
                  className="flex w-full items-stretch p-3 leading-5"
                >
                  <div className="relative">
                    {isMobile && (
                      <span
                        onClick={() =>
                          handleShare(likedListing.id, likedListing.title)
                        }
                        className="absolute right-0 top-0 z-20 cursor-pointer p-2"
                      >
                        <ShareIcon className="fun-svg-shadow-gray h-6 fill-white" />
                      </span>
                    )}
                    <Link
                      className="relative inline-block h-[6rem] w-[8rem] cursor-pointer lg:h-[8rem] lg:w-[12rem]"
                      href={`/listings/${likedListing.id}?title=${getUrlSlug(likedListing.title)}`}
                      scroll={false}
                      aria-label={likedListing.title}
                      prefetch={true}
                      onClick={() => {
                        updateMenu({ isLikeMenuActive: false });
                      }}
                    >
                      {likedListing.img_src && (
                        <FunImage
                          src={likedListing.img_src}
                          alt={likedListing.title}
                        />
                      )}
                    </Link>
                  </div>
                  <div className="flex w-full">
                    <div className="flex-start flex w-full flex-col pl-3 pt-0 text-[1.25rem]">
                      {likedListing.type && (
                        <span
                          className={`fun-${likedListing.type}-color mb-2 flex w-fit flex-nowrap items-center justify-center p-[.3rem] px-[.4rem] text-[.8rem] font-[500] tracking-[1] text-white lg:p-[.4rem] lg:px-[.5rem] lg:text-[1rem]`}
                        >
                          {getIcon(likedListing.type)}
                          {likedListing.subtype && (
                            <span className="ml-0 whitespace-nowrap border-l-2 pl-2 leading-[1]">
                              {formatTag(likedListing.subtype)}
                            </span>
                          )}
                        </span>
                      )}

                      <div className="flex w-full justify-between">
                        <Link
                          className={`${likedListing.date ? "line-clamp-3" : "line-clamp-4"} mb-1 text-[.9rem] font-[500] leading-[1.2] hover:underline lg:mb-2 lg:text-[1.1rem]`}
                          href={`/listings/${likedListing.id}?title=${getUrlSlug(likedListing.title)}`}
                          scroll={false}
                          aria-label={likedListing.title}
                          prefetch={true}
                          onClick={() => {
                            updateMenu({ isLikeMenuActive: false });
                          }}
                        >
                          {likedListing.title}
                        </Link>
                        <span
                          className="cursor-pointer self-center p-2 underline lg:p-4"
                          aria-label={`remove ${likedListing.title} from liked listings`}
                          onClick={(e) => {
                            e.currentTarget
                              .closest('[fun-id="fun-like-item"]')
                              ?.classList.add(
                                "duration-[400ms]",
                                "ease-linear",
                                "opacity-0",
                                "transition-opacity",
                              );
                            setTimeout(() => {
                              removeLikedListing(likedListing);
                            }, 600);
                          }}
                        >
                          <TrashIcon className="size-6" />
                        </span>
                      </div>
                      {likedListing.date && appStore.city?.timezone && (
                        <span className="whitespace-nowrap text-[.9rem] font-[500] lg:text-[1rem]">
                          {formatInTimeZone(
                            likedListing.date,
                            appStore.city?.timezone,
                            "M-dd-yyyy h:mmaa",
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
          <span
            ref={likeMenuRemoveBtnRef}
            className="align-center relative z-30 flex w-full cursor-pointer justify-center bg-orange p-4 font-[600] text-beige lg:p-6"
            aria-label="remove all liked listings"
            onClick={(e) => {
              const removeSecs = 1500;
              const cardAnimationSecs = 300;
              const els = document.querySelectorAll('[fun-id="fun-like-item"]');
              els.forEach((el, i) => {
                setTimeout(
                  () => {
                    el.classList.add(
                      `duration-[${cardAnimationSecs}ms]`,
                      "ease-linear",
                      "opacity-0",
                      "transition-opacity",
                    );
                  },
                  ((removeSecs - cardAnimationSecs) / els.length) *
                    (els.length - i),
                );
              });

              // delay for animation
              setTimeout(() => {
                removeAllLikedListings();
              }, removeSecs);
            }}
          >
            Remove ALL
          </span>
        </>
      )}
    </div>
  );
}
